import { useEffect, useState } from "react";
import { Check, PartyPopper, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const unlockedFeatures = [
  "Unlimited goals and habits",
  "Weekly AI Coach insights",
  "Full mentor access",
  "90+ day habit history",
];

interface WelcomeProModalProps {
  open: boolean;
  onClose: () => void;
}

export function WelcomeProModal({ open, onClose }: WelcomeProModalProps) {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (open) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        {/* Confetti animation overlay */}
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
            <div className="confetti-container">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="confetti-piece"
                  style={{
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 0.5}s`,
                    backgroundColor: ['hsl(var(--primary))', 'hsl(var(--accent))', '#FFD700', '#FF6B6B', '#4ECDC4'][Math.floor(Math.random() * 5)],
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <DialogHeader className="text-center pb-2">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-accent/20">
            <PartyPopper className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-2xl font-bold">
            Welcome to Pro! 🎉
          </DialogTitle>
          <DialogDescription className="text-base">
            You now have unlimited access to MomentumEQ.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-muted-foreground mb-4 text-center">
            Here's what's unlocked:
          </p>
          <ul className="space-y-3">
            {unlockedFeatures.map((feature) => (
              <li key={feature} className="flex items-center gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Check className="h-3.5 w-3.5 text-primary" />
                </div>
                <span className="text-sm font-medium">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <Button 
          onClick={onClose} 
          className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90"
        >
          <Sparkles className="mr-2 h-4 w-4" />
          Start Exploring
        </Button>
      </DialogContent>

      <style>{`
        .confetti-container {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }
        .confetti-piece {
          position: absolute;
          width: 8px;
          height: 8px;
          top: -10px;
          opacity: 0;
          animation: confetti-fall 2s ease-out forwards;
        }
        .confetti-piece:nth-child(odd) {
          border-radius: 50%;
        }
        .confetti-piece:nth-child(even) {
          border-radius: 2px;
          transform: rotate(45deg);
        }
        @keyframes confetti-fall {
          0% {
            opacity: 1;
            top: -10px;
            transform: translateX(0) rotate(0deg);
          }
          100% {
            opacity: 0;
            top: 100%;
            transform: translateX(var(--drift, 20px)) rotate(720deg);
          }
        }
        .confetti-piece:nth-child(1) { --drift: -30px; }
        .confetti-piece:nth-child(2) { --drift: 25px; }
        .confetti-piece:nth-child(3) { --drift: -15px; }
        .confetti-piece:nth-child(4) { --drift: 40px; }
        .confetti-piece:nth-child(5) { --drift: -25px; }
        .confetti-piece:nth-child(6) { --drift: 15px; }
        .confetti-piece:nth-child(7) { --drift: -40px; }
        .confetti-piece:nth-child(8) { --drift: 30px; }
        .confetti-piece:nth-child(9) { --drift: -20px; }
        .confetti-piece:nth-child(10) { --drift: 35px; }
        .confetti-piece:nth-child(11) { --drift: -35px; }
        .confetti-piece:nth-child(12) { --drift: 20px; }
        .confetti-piece:nth-child(13) { --drift: -10px; }
        .confetti-piece:nth-child(14) { --drift: 45px; }
        .confetti-piece:nth-child(15) { --drift: -45px; }
        .confetti-piece:nth-child(16) { --drift: 10px; }
        .confetti-piece:nth-child(17) { --drift: -50px; }
        .confetti-piece:nth-child(18) { --drift: 50px; }
        .confetti-piece:nth-child(19) { --drift: -5px; }
        .confetti-piece:nth-child(20) { --drift: 5px; }
      `}</style>
    </Dialog>
  );
}
